import { useEffect, useRef, useState, memo } from 'react';
import Box from '@mui/material/Box';
import _ from 'lodash';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Button, LinearProgress, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { useRequest } from '@main/hooks';
import { v4 as uuidv4 } from 'uuid';
import FileModel from '../model/FileModel';
import { formatBytes } from '../Utils/conversors';

const funcPrototype = () => new Promise();

const getDefaults = () => ({
  title: 'title',
});

const defaultErrEnums = {
  'too-many-files': 'Solo se permite cargar un archivo',
  'file-too-large': 'El archivo no debe rebasar los 25000kB',
  'unsupported-format': 'El formato de archivo no es compatible',
  'upload-failed': 'La carga del archivo ha fallado, por favor inténtelo de nuevo',
  'file-invalid-type': 'Formato de archivo no valido',
};

const commonItemFileClasses = 'flex flex-col justify-center mx-16 w-full';
const infoItemFileClasses = 'text-nowrap overflow-hidden text-ellipsis';
const uploadingItemFileClasses = 'min-w-[150px] text-center sm:text-start';

/**
 * Componente que representa un archivo dentro de una lista de subida.
 *
 * Muestra la información del archivo (nombre, tamaño), su progreso de carga,
 * y permite eliminarlo. Puede realizar cargas en un solo paso o en chunks.
 *
 * Utiliza funcionalidades de MUI y hooks personalizados para manejar peticiones.
 *
 * @component
 *
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.className] - Clases CSS adicionales para aplicar al contenedor principal.
 * @param {Object} [props.setFile] - Objeto del archivo cargado que incluye `wasSavedToServer`, `file`, y `metadata`.
 * @param {Object} [props.fileEqualsTo] - Objeto de referencia para comparar campos del archivo (`title`, etc.).
 * @param {Function} props.storeRequest - Función async para almacenar el archivo en el servidor.
 * @param {Function} props.deleteRequest - Función async para eliminar el archivo del servidor.
 * @param {Object} props.formDataParams - Parámetros extra para incluir en el `FormData` al hacer la petición.
 * @param {boolean} [props.uploadAsChunk=false] - Si `true`, el archivo se carga en múltiples partes (chunks).
 * @param {number} [props.position=0] - Índice del archivo en la lista para poder identificarlo en callbacks como `onSlice`.
 * @param {Function} [props.onMessage] - Función callback que recibe mensajes de estado o error.
 * @param {number} [props.chunkSize=100] - Tamaño del chunk (en MB) usado para dividir el archivo si `uploadAsChunk` está activado.
 * @param {Function} [props.onChangeState] - Callback que recibe el estado actualizado del archivo (cuando ha sido guardado).
 * @param {Function} [props.onSlice] - Callback que se dispara para eliminar el archivo del estado externo (por índice).
 *
 * @returns {JSX.Element} Componente visual de un archivo con controles y estado de carga.
 */
const ItemFile = memo(
  ({
    className,
    setFile,
    fileEqualsTo,
    storeRequest,
    deleteRequest,
    formDataParams,
    uploadAsChunk = false,
    position = 0,
    onMessage = () => {},
    chunkSize = 100,
    onChangeState = () => {},
    onSlice = () => {},
    disabled = false,
  }) => {
    const [fileRaw, setFileRaw] = useState(undefined);
    const [fileObject, setFileObject] = useState(undefined);
    const [fileName, setFileName] = useState(undefined);
    const [size, setSize] = useState(0);
    const [wasSavedToServer, setWasSavedToServer] = useState(false);
    const [progressChunkValue, setProgressChunkValue] = useState(0);
    const [isLoadingChunks, setIsLoadingChunks] = useState(false);
    const abortControllerRef = useRef(null);

    const {
      makeRequest: storeFile,
      isLoading: isLoadingStore,
      response: responseStore,
      status: statusStore,
    } = useRequest(storeRequest);

    const { makeRequest: deleteFile } = useRequest(deleteRequest);

    useEffect(() => {
      if (!setFile) return;
      if (!_.has(setFile, 'wasSavedToServer')) return;
      setWasSavedToServer(setFile.wasSavedToServer);
      setFileRaw(setFile?.file);
      setFileObject(setFile?.metadata);
    }, [setFile]);

    const abortUpload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

    const onUploadProgress = ({ loaded, total }) => {
      const fetchingPercent = Math.floor((loaded * 100) / total);
      setProgressChunkValue(fetchingPercent);
    };

    const handleResponseRequest = (reqRes) => {
      if (reqRes === undefined || reqRes === null) {
        onSlice(position);
        return;
      }
      onChangeState({
        wasSavedToServer: true,
        metadata: reqRes,
        file: null,
      });
    };

    const uploadAsSingle = (file) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;

      const formData = new FormData();
      const alterProps = {
        ...formDataParams,
        title: formDataParams?.title || formDataParams?.name || file.name,
      };
      const extraMetadata = FileModel(alterProps);
      Object.keys(extraMetadata).forEach((paramKey) => {
        formData.append(`${paramKey}`, extraMetadata[paramKey]);
      });
      formData.append('file', file);
      if (typeof storeRequest === 'function') {
        storeFile(formData, { onUploadProgress, signal })
          .then(({ isOk, errorContent }) => {
            const message = isOk
              ? 'Archivo cargado correctamente'
              : errorContent?.message || 'Ha ocurrido un error';
            console.debug(message);
            onMessage(message);
          })
          .finally(() => {
            setProgressChunkValue(0);
          });
      }
    };

    const recursiveLoad = async (file, extraMetadata) => {
      if (file === undefined) return;
      abortControllerRef.current = new AbortController();

      setIsLoadingChunks(true);
      const chunkBytesSize = chunkSize * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkBytesSize);

      console.info(
        `uuid: ${extraMetadata.uuid} \nname: ${file.name} \nsize: ${
          file.size / 1024 / 1024
        }Mbytes \ntotalChunks: ${totalChunks}`
      );

      async function loadNext(currentChunk = 0) {
        if (currentChunk + 1 > totalChunks) {
          setProgressChunkValue(0);
          setIsLoadingChunks(false);
          return;
        }
        const index = currentChunk;
        const start = index * chunkBytesSize;
        const end = Math.min(file.size, start + chunkBytesSize);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        if (!_.isEmpty(extraMetadata)) {
          Object.keys(extraMetadata).forEach((paramKey) => {
            formData.append(`${paramKey}`, extraMetadata[paramKey]);
          });
        }
        formData.append('file', chunk);
        formData.append('fileName', file.name);
        formData.append('chunkIndex', index.toString());
        formData.append('totalChunks', totalChunks.toString());
        const chunkSizeTemp = end - start;
        console.info(
          `chunk: ${index + 1} de ${totalChunks} | chunkSize: ${chunkSizeTemp / 1024 / 1024}Mbytes`
        );
        try {
          const { isOk, successContent, errorContent } = await storeFile(formData, {
            onUploadProgress: ({ loaded }) =>
              onUploadProgress({ loaded: loaded + start, total: file.size }),
            signal: abortControllerRef.current.signal,
          });
          if (!isOk) {
            setProgressChunkValue(0);
            setIsLoadingChunks(false);
            onMessage(errorContent?.message || 'Ha ocurrido un error');
            return;
          }
          if (currentChunk + 1 >= totalChunks) {
            handleResponseRequest(successContent);
            onMessage('Archivo cargado correctamente');
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            console.debug('Subida abortada por el usuario');
          } else {
            console.error(err);
            onMessage('Ocurrió un error durante la carga');
          }
          setProgressChunkValue(0);
          setIsLoadingChunks(false);
          return;
        }

        const tryTimeOutFetch = setTimeout(async () => {
          await loadNext(currentChunk + 1);
          clearTimeout(tryTimeOutFetch);
        }, 1000);
      }

      await loadNext(0);
    };

    const uploadAsChunks = async (file) => {
      const uuid = uuidv4();
      const alterProps = {
        ...formDataParams,
        uuid,
        title: formDataParams?.title || formDataParams?.name || file.name,
      };
      const extraMetadata = FileModel(alterProps);
      await recursiveLoad(file, extraMetadata);
    };

    const handleUpload = (file) => {
      if (!uploadAsChunk) {
        uploadAsSingle(file);
        return;
      }
      uploadAsChunks(file);
    };

    const handleDeleteFile = () => {
      if (isLoadingChunks || isLoadingStore) {
        abortUpload();
        onSlice(position);
        return;
      }
      if (typeof deleteRequest === 'function') {
        deleteFile(fileObject)
          .then(({ isOk }) => {
            const message = isOk
              ? 'Archivo borrado correctamente'
              : 'Ha ocurrido un error al eliminar el archivo';
            console.debug(message);
            onMessage(message);
            if (isOk) {
              onSlice(position);
            }
          })
          .catch((err) => {
            console.error(err);
          });
        return;
      }
      onSlice(position);
    };

    useEffect(() => {
      if (fileObject === undefined || fileRaw === undefined) return;
      if (wasSavedToServer === true) {
        setSize(fileObject?.size);
        const reference = _.merge({}, getDefaults(), fileEqualsTo);
        setFileName(fileObject[reference.title]);
        return;
      }
      setSize(fileRaw.size);
      setFileName(fileRaw.name);
    }, [fileObject, fileRaw, wasSavedToServer]);

    useEffect(() => {
      if (uploadAsChunk) return;
      if (statusStore !== 'SUCCESS') return;
      handleResponseRequest(responseStore);
    }, [responseStore, statusStore, uploadAsChunk]);

    useEffect(() => {
      return () => {
        abortUpload();
      };
    }, []);

    useEffect(() => {
      if (!fileRaw || wasSavedToServer === true) return;
      handleUpload(fileRaw);
    }, [fileRaw, wasSavedToServer]);

    return (
      <Box className={clsx('flex flex-row items-center min-h-[40px] w-full px-24', className)}>
        <div className="py-8">
          <InsertDriveFileOutlinedIcon sx={{ width: 'auto', height: '6rem', color: '#9ca3af' }} />
        </div>
        {isLoadingStore || isLoadingChunks ? (
          <div className={clsx(commonItemFileClasses, uploadingItemFileClasses)}>
            <LinearProgress
              variant="determinate"
              value={progressChunkValue}
              className="w-full mt-10"
              color="secondary"
            />
            <div className="flex w-full justify-between">
              <Typography variant="caption" className="my-4">
                Subiendo archivo...
              </Typography>
              <Typography variant="caption" className="my-4">{`${progressChunkValue}%`}</Typography>
            </div>
          </div>
        ) : (
          <div className={clsx(commonItemFileClasses, infoItemFileClasses)}>
            <Typography variant="caption">{fileName ?? 'Unknown file'}</Typography>
            {(typeof size === 'string' || typeof size === 'number') && (
              <Typography variant="caption">{formatBytes(size)}</Typography>
            )}
          </div>
        )}
        {!disabled && (
          <div className="flex justify-center items-center w-[60px] h-full" style={{ zIndex: 1000 }}>
            <Tooltip title="Eliminar archivo">
              <Button
                className="my-3"
                onClick={() => handleDeleteFile()}
                size="small"
                variant="outlined"
                color="primary"
                sx={{
                  minWidth: '0.8rem',
                  padding: '0.36rem',
                }}
              >
                <CloseIcon />
              </Button>
            </Tooltip>
          </div>
        )}
      </Box>
    );
  }
);

export default ItemFile;
